/**
 * @description
 * Domain analytics panel component with statistics and visualizations.
 * Displays domain distribution charts, statistics cards, and top domains table.
 *
 * @author AI Developer <ai@byteable.ai>
 * @date January 8, 2025
 * @see admin/src/frontend/components/vscode-lens/DomainAnalytics.test.tsx
 */

import { useState, useMemo } from 'react';
import { BarChart3, ChevronDown, ChevronUp, PieChart, TrendingUp, Users } from 'lucide-react';
import { BarChart, Bar, PieChart as RechartsPie, Pie, Cell, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts';
import type { DomainAggregation, DomainStatistics, DomainFilterConfig, VSCodeLensTable, StreamRecord } from '../../../types/vscode-lens';
import { getDomainColor, formatDomain } from '../../utils/domainUtils';

interface DomainAnalyticsProps {
  aggregation: DomainAggregation;
  statistics: DomainStatistics | null;
  filterConfig: DomainFilterConfig;
  onFilterChange: (config: DomainFilterConfig) => void;
  table: VSCodeLensTable;
  rawData: StreamRecord[];
}

/**
 * Domain analytics panel component
 */
export function DomainAnalytics({
  aggregation,
  statistics,
  filterConfig,
  onFilterChange,
  table,
  rawData,
}: DomainAnalyticsProps) {
  const [isExpanded, setIsExpanded] = useState(true);
  const [chartType, setChartType] = useState<'bar' | 'pie'>('bar');

  // Generate table-specific chart data for domains (moved before early return)
  const tableSpecificChartData = useMemo(() => {
    if (!rawData.length || !aggregation || aggregation.domains.length === 0) return null;

    const { topDomains } = aggregation;
    switch (table) {
      case 'commit_events':
        return generateDomainCommitChartData(rawData, topDomains);
      case 'conversation_messages':
        return generateDomainMessageChartData(rawData, topDomains);
      case 'llm_completions':
        return generateDomainCompletionChartData(rawData, topDomains);
      default:
        return null;
    }
  }, [table, rawData, aggregation]);

  if (!aggregation || aggregation.domains.length === 0) {
    return null;
  }

  const { domains, topDomains } = aggregation;

  // Prepare chart data (top 10 domains)
  const chartData = topDomains.map((d, index) => ({
    domain: d.domain.length > 20 ? d.domain.substring(0, 17) + '...' : d.domain,
    fullDomain: d.domain,
    count: d.count,
    percentage: d.percentage,
    color: getDomainColor(d.domain, index),
  }));

  const handleDomainClick = (domain: string) => {
    // Toggle domain selection
    const newSelected = new Set(filterConfig.selectedDomains);
    if (newSelected.has(domain)) {
      newSelected.delete(domain);
    } else {
      newSelected.add(domain);
    }
    
    onFilterChange({
      ...filterConfig,
      mode: 'include',
      enabled: true,
      selectedDomains: newSelected,
    });
  };

  return (
    <div className="rounded-lg border bg-card">
      {/* Header */}
      <button
        onClick={() => setIsExpanded(!isExpanded)}
        className="w-full flex items-center justify-between p-4 hover:bg-accent/50 transition-colors"
      >
        <div className="flex items-center gap-2">
          <TrendingUp className="h-5 w-5 text-primary" />
          <h2 className="text-lg font-semibold">Domain Analytics</h2>
          <span className="text-sm text-muted-foreground">
            ({domains.length} unique domains)
          </span>
        </div>
        {isExpanded ? (
          <ChevronUp className="h-5 w-5" />
        ) : (
          <ChevronDown className="h-5 w-5" />
        )}
      </button>

      {isExpanded && (
        <div className="p-4 pt-0 space-y-6">
          {/* Statistics Cards */}
          {statistics && (
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
              <StatCard
                icon={Users}
                label="Total Domains"
                value={statistics.totalDomains.toString()}
                color="blue"
              />
              <StatCard
                icon={BarChart3}
                label="Avg Records/Domain"
                value={Math.round(statistics.averageRecordsPerDomain).toString()}
                color="green"
              />
              <StatCard
                icon={TrendingUp}
                label="Largest Domain"
                value={statistics.largestDomain?.domain || 'N/A'}
                subtitle={statistics.largestDomain ? `${statistics.largestDomain.count} records` : undefined}
                color="purple"
              />
              <StatCard
                icon={PieChart}
                label="Concentration"
                value={`${statistics.domainConcentration.toFixed(1)}%`}
                subtitle="in top domain"
                color="orange"
              />
            </div>
          )}

          {/* Table-Specific Chart */}
          <div className="flex items-center justify-between">
            <h3 className="text-sm font-medium">
              {table === 'commit_events' && 'Commit Activity by Domain'}
              {table === 'conversation_messages' && 'Message Activity by Domain'}
              {table === 'llm_completions' && 'Completion Activity by Domain'}
            </h3>
            <div className="flex gap-2">
              <button
                onClick={() => setChartType('bar')}
                className={`px-3 py-1.5 text-xs rounded-md border transition-colors ${
                  chartType === 'bar'
                    ? 'bg-primary text-primary-foreground border-primary'
                    : 'bg-background border-input hover:bg-accent'
                }`}
              >
                <BarChart3 className="h-3 w-3 inline mr-1" />
                Bar Chart
              </button>
              <button
                onClick={() => setChartType('pie')}
                className={`px-3 py-1.5 text-xs rounded-md border transition-colors ${
                  chartType === 'pie'
                    ? 'bg-primary text-primary-foreground border-primary'
                    : 'bg-background border-input hover:bg-accent'
                }`}
              >
                <PieChart className="h-3 w-3 inline mr-1" />
                Pie Chart
              </button>
            </div>
          </div>

          {/* Table-Specific Charts */}
          <div className="h-80">
            {tableSpecificChartData ? (
              <ResponsiveContainer width="100%" height="100%">
                {tableSpecificChartData.chartType === 'bar' ? (
                  <BarChart data={tableSpecificChartData.data}>
                    <CartesianGrid strokeDasharray="3 3" className="stroke-muted" />
                    <XAxis 
                      dataKey="label" 
                      className="text-xs"
                      angle={-45}
                      textAnchor="end"
                      height={80}
                    />
                    <YAxis className="text-xs" />
                    <Tooltip
                      content={({ active, payload }) => {
                        if (active && payload && payload.length) {
                          const data = payload[0].payload;
                          return (
                            <div className="rounded-lg border bg-card p-3 shadow-lg">
                              <p className="font-medium">{data.fullLabel}</p>
                              <p className="text-sm text-muted-foreground">
                                {data.value} {tableSpecificChartData.unit}
                              </p>
                            </div>
                          );
                        }
                        return null;
                      }}
                    />
                    <Bar
                      dataKey="value"
                      fill="#3b82f6"
                      radius={[4, 4, 0, 0]}
                      onClick={(data) => {
                        if (data && typeof data === 'object' && 'domain' in data) {
                          handleDomainClick(data.domain as string);
                        }
                      }}
                      className="cursor-pointer"
                    >
                      {tableSpecificChartData.data.map((entry, index) => (
                        <Cell key={`cell-${index}`} fill={entry.color} />
                      ))}
                    </Bar>
                  </BarChart>
                ) : (
                  <RechartsPie>
                    <Pie
                      data={tableSpecificChartData.data}
                      cx="50%"
                      cy="50%"
                      labelLine={false}
                      label={(props) => {
                        const payload = props.payload as { label?: string; percentage?: number } | undefined;
                        const label = payload?.label || '';
                        const percentage = payload?.percentage || 0;
                        return `${label} (${percentage.toFixed(1)}%)`;
                      }}
                      outerRadius={100}
                      fill="#8884d8"
                      dataKey="value"
                      onClick={(data) => {
                        if (data && typeof data === 'object' && 'domain' in data) {
                          handleDomainClick(data.domain as string);
                        }
                      }}
                      className="cursor-pointer"
                    >
                      {tableSpecificChartData.data.map((entry, index) => (
                        <Cell key={`cell-${index}`} fill={entry.color} />
                      ))}
                    </Pie>
                    <Tooltip
                      content={({ active, payload }) => {
                        if (active && payload && payload.length) {
                          const data = payload[0].payload;
                          return (
                            <div className="rounded-lg border bg-card p-3 shadow-lg">
                              <p className="font-medium">{data.fullLabel}</p>
                              <p className="text-sm text-muted-foreground">
                                {data.value} {tableSpecificChartData.unit}
                              </p>
                            </div>
                          );
                        }
                        return null;
                      }}
                    />
                  </RechartsPie>
                )}
              </ResponsiveContainer>
            ) : (
              // Fallback to domain distribution chart
              chartType === 'bar' ? (
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={chartData}>
                    <CartesianGrid strokeDasharray="3 3" className="stroke-muted" />
                    <XAxis 
                      dataKey="domain" 
                      className="text-xs"
                      angle={-45}
                      textAnchor="end"
                      height={80}
                    />
                    <YAxis className="text-xs" />
                    <Tooltip
                      content={({ active, payload }) => {
                        if (active && payload && payload.length) {
                          const data = payload[0].payload;
                          return (
                            <div className="rounded-lg border bg-card p-3 shadow-lg">
                              <p className="font-medium">{data.fullDomain}</p>
                              <p className="text-sm text-muted-foreground">
                                {data.count} records ({data.percentage.toFixed(1)}%)
                              </p>
                            </div>
                          );
                        }
                        return null;
                      }}
                    />
                    <Bar
                      dataKey="count"
                      fill="#3b82f6"
                      radius={[4, 4, 0, 0]}
                      onClick={(data) => {
                        if (data && typeof data === 'object' && 'fullDomain' in data) {
                          handleDomainClick(data.fullDomain as string);
                        }
                      }}
                      className="cursor-pointer"
                    >
                      {chartData.map((entry, index) => (
                        <Cell key={`cell-${index}`} fill={entry.color} />
                      ))}
                    </Bar>
                  </BarChart>
                </ResponsiveContainer>
              ) : (
                <ResponsiveContainer width="100%" height="100%">
                  <RechartsPie>
                    <Pie
                      data={chartData}
                      cx="50%"
                      cy="50%"
                      labelLine={false}
                      label={(props) => {
                        const payload = props.payload as { domain?: string; percentage?: number } | undefined;
                        const domain = payload?.domain || '';
                        const percentage = payload?.percentage || 0;
                        return `${domain} (${percentage.toFixed(1)}%)`;
                      }}
                      outerRadius={100}
                      fill="#8884d8"
                      dataKey="count"
                      onClick={(data) => {
                        if (data && typeof data === 'object' && 'fullDomain' in data) {
                          handleDomainClick(data.fullDomain as string);
                        }
                      }}
                      className="cursor-pointer"
                    >
                      {chartData.map((entry, index) => (
                        <Cell key={`cell-${index}`} fill={entry.color} />
                      ))}
                    </Pie>
                    <Tooltip
                      content={({ active, payload }) => {
                        if (active && payload && payload.length) {
                          const data = payload[0].payload;
                          return (
                            <div className="rounded-lg border bg-card p-3 shadow-lg">
                              <p className="font-medium">{data.fullDomain}</p>
                              <p className="text-sm text-muted-foreground">
                                {data.count} records ({data.percentage.toFixed(1)}%)
                              </p>
                            </div>
                          );
                        }
                        return null;
                      }}
                    />
                  </RechartsPie>
                </ResponsiveContainer>
              )
            )}
          </div>

          {/* Top Domains Table */}
          <div>
            <h3 className="text-sm font-medium mb-3">Top 10 Domains</h3>
            <div className="rounded-lg border overflow-hidden">
              <table className="w-full text-sm">
                <thead className="bg-muted/50 border-b">
                  <tr>
                    <th className="px-4 py-2 text-left font-medium">#</th>
                    <th className="px-4 py-2 text-left font-medium">Domain</th>
                    <th className="px-4 py-2 text-right font-medium">Records</th>
                    <th className="px-4 py-2 text-right font-medium">Percentage</th>
                    <th className="px-4 py-2 text-center font-medium">Action</th>
                  </tr>
                </thead>
                <tbody>
                  {topDomains.map((domain, index) => {
                    const isSelected = filterConfig.selectedDomains.has(domain.domain);
                    return (
                      <tr
                        key={domain.domain}
                        className="border-b last:border-0 hover:bg-muted/30 transition-colors"
                      >
                        <td className="px-4 py-3 text-muted-foreground">{index + 1}</td>
                        <td className="px-4 py-3 font-medium" title={domain.domain}>
                          {formatDomain(domain.domain, 40)}
                        </td>
                        <td className="px-4 py-3 text-right">{domain.count.toLocaleString()}</td>
                        <td className="px-4 py-3 text-right">{domain.percentage.toFixed(2)}%</td>
                        <td className="px-4 py-3 text-center">
                          <button
                            onClick={() => handleDomainClick(domain.domain)}
                            className={`px-3 py-1 text-xs rounded-md border transition-colors ${
                              isSelected
                                ? 'bg-primary text-primary-foreground border-primary'
                                : 'bg-background border-input hover:bg-accent'
                            }`}
                          >
                            {isSelected ? 'Selected' : 'Select'}
                          </button>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>

          {/* Help Text */}
          <p className="text-xs text-muted-foreground">
            Click on chart bars or use the Select buttons to filter data by domain.
          </p>
        </div>
      )}
    </div>
  );
}

/**
 * Generate domain commit-specific chart data
 */
function generateDomainCommitChartData(data: StreamRecord[], topDomains: { domain: string; count: number; percentage: number }[]) {
  const domainCommitData = new Map<string, { commits: number; linesAdded: number; linesDeleted: number; filesChanged: number }>();
  
  data.forEach(record => {
    const user = record.user_email || record.author_email || 'unknown';
    const domain = typeof user === 'string' && user.includes('@') ? user.split('@')[1] : 'unknown';
    const existing = domainCommitData.get(domain) || { commits: 0, linesAdded: 0, linesDeleted: 0, filesChanged: 0 };
    
    existing.commits++;
    existing.linesAdded += typeof record.lines_added === 'number' ? record.lines_added : 0;
    existing.linesDeleted += typeof record.lines_deleted === 'number' ? record.lines_deleted : 0;
    existing.filesChanged += typeof record.files_changed === 'number' ? record.files_changed : (typeof record.files_modified === 'number' ? record.files_modified : 0);
    
    domainCommitData.set(domain, existing);
  });

  const chartData = topDomains.map((domain, index) => {
    const commitData = domainCommitData.get(domain.domain) || { commits: 0, linesAdded: 0, linesDeleted: 0, filesChanged: 0 };
    return {
      label: domain.domain.length > 15 ? domain.domain.substring(0, 12) + '...' : domain.domain,
      fullLabel: domain.domain,
      domain: domain.domain,
      value: commitData.commits,
      linesAdded: commitData.linesAdded,
      linesDeleted: commitData.linesDeleted,
      filesChanged: commitData.filesChanged,
      color: getDomainColor(domain.domain, index),
      percentage: domain.percentage,
    };
  });

  return {
    chartType: 'bar' as const,
    data: chartData,
    unit: 'commits',
  };
}

/**
 * Generate domain message-specific chart data
 */
function generateDomainMessageChartData(data: StreamRecord[], topDomains: { domain: string; count: number; percentage: number }[]) {
  const domainMessageData = new Map<string, { messages: number; totalLength: number; avgLength: number }>();
  
  data.forEach(record => {
    const user = record.user_email || record.author_email || 'unknown';
    const domain = typeof user === 'string' && user.includes('@') ? user.split('@')[1] : 'unknown';
    const existing = domainMessageData.get(domain) || { messages: 0, totalLength: 0, avgLength: 0 };
    const content = record.content || record.message || record.text || '';
    const length = typeof content === 'string' ? content.length : 0;
    
    existing.messages++;
    existing.totalLength += length;
    existing.avgLength = existing.totalLength / existing.messages;
    
    domainMessageData.set(domain, existing);
  });

  const chartData = topDomains.map((domain, index) => {
    const messageData = domainMessageData.get(domain.domain) || { messages: 0, totalLength: 0, avgLength: 0 };
    return {
      label: domain.domain.length > 15 ? domain.domain.substring(0, 12) + '...' : domain.domain,
      fullLabel: domain.domain,
      domain: domain.domain,
      value: messageData.messages,
      avgLength: Math.round(messageData.avgLength),
      totalLength: messageData.totalLength,
      color: getDomainColor(domain.domain, index),
      percentage: domain.percentage,
    };
  });

  return {
    chartType: 'bar' as const,
    data: chartData,
    unit: 'messages',
  };
}

/**
 * Generate domain completion-specific chart data
 */
function generateDomainCompletionChartData(data: StreamRecord[], topDomains: { domain: string; count: number; percentage: number }[]) {
  const domainCompletionData = new Map<string, { completions: number; totalTokens: number; avgTokens: number }>();
  
  data.forEach(record => {
    const user = record.user_email || record.author_email || 'unknown';
    const domain = typeof user === 'string' && user.includes('@') ? user.split('@')[1] : 'unknown';
    const existing = domainCompletionData.get(domain) || { completions: 0, totalTokens: 0, avgTokens: 0 };
    const tokens = typeof record.tokens === 'number' ? record.tokens : (typeof record.total_tokens === 'number' ? record.total_tokens : 0);
    
    existing.completions++;
    existing.totalTokens += tokens;
    existing.avgTokens = existing.totalTokens / existing.completions;
    
    domainCompletionData.set(domain, existing);
  });

  const chartData = topDomains.map((domain, index) => {
    const completionData = domainCompletionData.get(domain.domain) || { completions: 0, totalTokens: 0, avgTokens: 0 };
    return {
      label: domain.domain.length > 15 ? domain.domain.substring(0, 12) + '...' : domain.domain,
      fullLabel: domain.domain,
      domain: domain.domain,
      value: completionData.completions,
      avgTokens: Math.round(completionData.avgTokens),
      totalTokens: completionData.totalTokens,
      color: getDomainColor(domain.domain, index),
      percentage: domain.percentage,
    };
  });

  return {
    chartType: 'bar' as const,
    data: chartData,
    unit: 'completions',
  };
}

/**
 * Statistics card component
 */
function StatCard({
  icon: Icon,
  label,
  value,
  subtitle,
  color,
}: {
  icon: React.ElementType;
  label: string;
  value: string;
  subtitle?: string;
  color: 'blue' | 'green' | 'purple' | 'orange';
}) {
  const colorClasses = {
    blue: 'bg-blue-100 dark:bg-blue-950 text-blue-700 dark:text-blue-300',
    green: 'bg-green-100 dark:bg-green-950 text-green-700 dark:text-green-300',
    purple: 'bg-purple-100 dark:bg-purple-950 text-purple-700 dark:text-purple-300',
    orange: 'bg-orange-100 dark:bg-orange-950 text-orange-700 dark:text-orange-300',
  };

  return (
    <div className="rounded-lg border bg-card p-4">
      <div className="flex items-center justify-between mb-2">
        <p className="text-sm font-medium text-muted-foreground">{label}</p>
        <div className={`p-2 rounded-md ${colorClasses[color]}`}>
          <Icon className="h-4 w-4" />
        </div>
      </div>
      <div>
        <p className="text-2xl font-bold" title={value}>
          {value.length > 20 ? value.substring(0, 17) + '...' : value}
        </p>
        {subtitle && (
          <p className="text-xs text-muted-foreground mt-1">{subtitle}</p>
        )}
      </div>
    </div>
  );
}

/*
 * © 2025 Byte Inc. All rights reserved.
 * This code is the confidential and proprietary information of Byte Inc.
 * You shall not disclose such Confidential Information and shall use it only in
 * accordance with the terms of the license agreement you entered into with Byte Inc.
 */